/**
 * Parses the `form_responses` payload returned by GET /jobs/data-capture/:jobId
 * into a per-form, per-section structure for display.
 *
 * The backend returns one row per `job_task_form` submission, each carrying its
 * own `form_name` and a `form_data` JSON blob whose sections carry a
 * `section_name`. Flattening those rows into a single list loses the form
 * boundaries and misattributes captured photos between forms (WRK-5765).
 */

/** A single question/answer pair, plus any photos captured against it. */
export interface ParsedFormAnswer {
  question: string;
  answer: string;
  /** Resolved presigned image URLs from an `s3_image` or `signature` control. Empty for other controls. */
  images: string[];
  /**
   * True when this answer came from a conditional branch rather than a question of
   * its own, so the panel can indent it under the question that revealed it.
   */
  nested?: boolean;
}

/** One section of a submitted form. */
export interface ParsedFormSection {
  /** `section_name` from the stored form_data. Empty when the form has none. */
  sectionName: string;
  answers: ParsedFormAnswer[];
}

/** One submitted form (one `job_task_form` row). */
export interface ParsedFormGroup {
  /** Stable key — the `job_task_form` row id, so repeat submissions stay distinct. */
  key: string;
  formName: string;
  submittedAt: string | null;
  sections: ParsedFormSection[];
}

/** The subset of the API row this parser reads. */
export interface FormResponseRow {
  id: number;
  form_name?: string | null;
  form_data?: unknown;
  created_time?: string | null;
}

const GENERIC_FORM_LABEL = 'Form';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function trimmedOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Resolve the heading for a submitted form. The `form` table join is reliable in
 * practice, but fall back through the embedded form_data name to a generic label
 * so a panel is never unlabelled.
 */
function resolveFormName(row: FormResponseRow, formData: Record<string, unknown>): string {
  return trimmedOrNull(row.form_name) ?? trimmedOrNull(formData.name) ?? GENERIC_FORM_LABEL;
}

/**
 * Normalise the many shapes `form_data` takes in the wild:
 * - a form object with a `sections` array (what the mobile app submits)
 * - a bare array of sections
 * - a single section object carrying `controls`
 * - a flat `{ question, answer }` object (legacy)
 */
function normaliseSections(formData: Record<string, unknown>): unknown[] {
  if (Array.isArray(formData)) {
    return formData;
  }
  return Array.isArray(formData.sections) ? formData.sections : [formData];
}

/**
 * Turn one control into an answer row, or null when it carries nothing to show.
 */
function parseControl(control: Record<string, unknown>): ParsedFormAnswer | null {
  const question = String(control.control_name ?? '');
  const controlType = String(control.control_type ?? '');
  const value = control.value;

  // Label-only controls are presentational headings, not answers.
  if (controlType === 'label') {
    return null;
  }

  if ((controlType === 's3_image' || controlType === 'signature') && value != null) {
    const items = Array.isArray(value) ? value : [value];
    const images: string[] = [];
    let capturedCount = 0;
    for (const entry of items) {
      const img = asRecord(entry);
      if (!img) {
        continue;
      }
      capturedCount += 1;
      if (typeof img.url === 'string') {
        images.push(img.url);
      }
    }
    // A captured item with no resolved url (presign failed, or legacy data) must
    // still be counted — reporting "0 photos captured" on a compliance form reads
    // as "no photo taken" rather than "photo failed to load" (CWL-301 review).
    const unavailable = capturedCount - images.length;
    const answer =
      unavailable > 0
        ? `${capturedCount} photo${capturedCount === 1 ? '' : 's'} captured – ${unavailable} unavailable`
        : `${capturedCount} photo${capturedCount === 1 ? '' : 's'} captured`;
    return { question, answer, images };
  }

  if (controlType === 'map' && Array.isArray(value)) {
    const coord = asRecord(value[0]);
    if (!coord) {
      return null;
    }
    return {
      question,
      answer: `${Number(coord.latitude ?? 0).toFixed(6)}, ${Number(coord.longitude ?? 0).toFixed(6)}`,
      images: [],
    };
  }

  if (Array.isArray(value)) {
    return {
      question,
      answer: value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', '),
      images: [],
    };
  }

  if (value != null) {
    return { question, answer: String(value), images: [] };
  }

  return null;
}

/**
 * A conditional_field stores the crew's answer as the chosen condition's name, and
 * the fields they then filled in live under that condition's `conditionalControls`.
 * Reading only the control's own `value` therefore loses every branch answer, even
 * though the mobile app submits them (SC-6511). Only the chosen branch is expanded:
 * the other branches keep whatever values a previous answer left behind, and showing
 * those would misreport what was captured.
 */
function parseBranchAnswers(control: Record<string, unknown>): ParsedFormAnswer[] {
  if (String(control.control_type ?? '') !== 'conditional_field') {
    return [];
  }
  const chosen = Array.isArray(control.value) ? control.value[0] : control.value;
  if (chosen == null || !Array.isArray(control.conditions)) {
    return [];
  }

  const answers: ParsedFormAnswer[] = [];
  for (const condition of control.conditions) {
    const cond = asRecord(condition);
    if (!cond || String(cond.condition_name ?? '') !== String(chosen)) {
      continue;
    }
    const branchControls = Array.isArray(cond.conditionalControls) ? cond.conditionalControls : [];
    for (const branchControl of branchControls) {
      const branch = asRecord(branchControl);
      if (!branch) {
        continue;
      }
      // Reuse parseControl so s3_image, map and array values format identically to
      // a top-level field of the same type.
      const parsed = parseControl(branch);
      if (parsed) {
        answers.push({ ...parsed, nested: true });
      }
    }
  }
  return answers;
}

/**
 * A group_controls control has no answer of its own: its fields live inside it, one set
 * per instance in `repeated_controls[].nested_controls` (repeatable) or directly in
 * `nested_controls`. Reading only the group's own `value` loses all of them, even though
 * the mobile app submits them and CORE receives them. Repeatable groups prefix each
 * question with the group name and instance number, so two instances of the same field
 * stay distinguishable.
 */
function parseGroupAnswers(control: Record<string, unknown>): ParsedFormAnswer[] {
  if (String(control.control_type ?? '') !== 'group_controls') {
    return [];
  }
  const groupName = String(control.control_name ?? control.group_name ?? '').trim();
  const repeatable = control.is_repeatable === true;

  // nested_controls is a flat list of controls, or a list of control lists.
  const flatten = (nested: unknown): unknown[] =>
    Array.isArray(nested) ? nested.flatMap((n) => (Array.isArray(n) ? n : [n])) : [];

  const instances: unknown[][] = repeatable
    ? (Array.isArray(control.repeated_controls) ? control.repeated_controls : []).map((instance) =>
        flatten(asRecord(instance)?.nested_controls),
      )
    : [flatten(control.nested_controls)];

  const answers: ParsedFormAnswer[] = [];
  instances.forEach((nestedControls, instanceIndex) => {
    const prefix = repeatable ? `${groupName} ${instanceIndex + 1}` : groupName;
    for (const nestedControl of nestedControls) {
      const nested = asRecord(nestedControl);
      if (!nested) {
        continue;
      }
      const label = (answer: ParsedFormAnswer): ParsedFormAnswer =>
        prefix ? { ...answer, question: `${prefix}: ${answer.question}` } : answer;
      const parsed = parseControl(nested);
      if (parsed) {
        answers.push(label(parsed));
      }
      answers.push(...parseBranchAnswers(nested).map(label));
    }
  });
  return answers;
}

/** Structural equality over JSON-like control data (object key order is irrelevant; array order is not). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;
  const recordA = asRecord(a);
  const recordB = asRecord(b);
  if (recordA && recordB) {
    const keysA = Object.keys(recordA);
    const keysB = Object.keys(recordB);
    return (
      keysA.length === keysB.length &&
      keysA.every((key) => key in recordB && deepEqual(recordA[key], recordB[key]))
    );
  }
  return false;
}

/**
 * One control-array per repeat copy, in order. Mobile's `getSubmitForm`
 * (`DynamicFormComponent.tsx` / `DynamicLayoutFormComponent.tsx`) unconditionally
 * appends the live `controls` value onto `repeated_sections` on submit, without
 * clearing `controls` — so `repeated_sections`'s last entry and `controls` are
 * always the SAME submitted copy by construction, never two independently
 * filled copies that happen to match (observed on CWL-323 staging job
 * 001008658528 — SAP received the "5th" copy twice, as two CurrentSSRAs items
 * with different MobileKeys). Deep-equality here is safe precisely because of
 * that guarantee: two genuinely distinct copies with identical answers never
 * reach this check, since the dedupe only ever compares the last entry against
 * the one value mobile is guaranteed to have duplicated.
 */
function instanceControls(sec: Record<string, unknown>): unknown[] {
  const repeated = Array.isArray(sec.repeated_sections) ? sec.repeated_sections : [];
  const base = Array.isArray(sec.controls) ? sec.controls : null;
  if (!base) return repeated;
  const last = repeated[repeated.length - 1];
  if (last !== undefined && deepEqual(last, base)) return repeated;
  return [...repeated, base];
}

const isBlankQId = (qId: unknown): boolean => qId == null || String(qId) === '';

const sameQId = (a: unknown, b: unknown): boolean =>
  !isBlankQId(a) && !isBlankQId(b) && String(a) === String(b);

function sectionControls(section: Record<string, unknown> | undefined): Record<string, unknown>[] {
  const controls = Array.isArray(section?.controls) ? section.controls : [];
  return controls.map(asRecord).filter((control): control is Record<string, unknown> => !!control);
}

/** The question after `qId` in order: the next in its section, else the next section's first. */
function nextQIdInOrder(sections: Record<string, unknown>[], qId: unknown): unknown {
  for (let s = 0; s < sections.length; s++) {
    const controls = sectionControls(sections[s]);
    const c = controls.findIndex((control) => sameQId(control.q_id, qId));
    if (c === -1) continue;
    return controls[c + 1]?.q_id ?? sectionControls(sections[s + 1])[0]?.q_id ?? null;
  }
  return null;
}

/**
 * Where a dynamic form goes after a control, as mobile routes it (`nextQIdIn`): a
 * conditional_field follows its answered branch, falling through to its own next_q_id
 * or the next question in order only when that branch has no next_q_id and is
 * end_form: false (CWL-304).
 */
function nextQIdIn(sections: Record<string, unknown>[], control: Record<string, unknown>): unknown {
  if (control.control_type !== 'conditional_field') return control.next_q_id;
  const chosen = Array.isArray(control.value) ? control.value[0] : control.value;
  const answered = (Array.isArray(control.conditions) ? control.conditions : [])
    .map(asRecord)
    .find((condition) => condition?.condition_name === chosen);
  if (answered) {
    if (!isBlankQId(answered.next_q_id)) return answered.next_q_id;
    if (answered.end_form !== false) return answered.next_q_id;
  }
  if (!isBlankQId(control.next_q_id)) return control.next_q_id;
  return nextQIdInOrder(sections, control.q_id);
}

/**
 * The controls on the route the engineer took through a dynamic form, walked as the
 * mobile Review screen walks it (`modifyDynamicFormForReview`): each section, and
 * each copy of a repeatable one, follows next_q_id on from where the last left off.
 * Changing an answer that branches (an SSRA Yes to No) leaves the old route's
 * answers stored, though the engineer never saw them on Review (CWL-323). The route
 * is derived from the stored form alone, so this also cleans up earlier submissions.
 *
 * Null, showing every answer as before, for a form that isn't dynamic (it doesn't
 * route by next_q_id) or whose questions carry no q_id.
 */
function controlsOnRoute(formData: Record<string, unknown>): Set<unknown> | null {
  if (formData.is_dynamic !== true || !Array.isArray(formData.sections)) return null;
  const sections = formData.sections
    .map(asRecord)
    .filter((section): section is Record<string, unknown> => !!section);
  const instancesOf = (section: Record<string, unknown>): unknown[][] => {
    const controls = Array.isArray(section.controls) ? section.controls : [];
    const repeated = Array.isArray(section.repeated_sections) ? section.repeated_sections : [];
    return repeated.length > 0
      ? [...repeated.filter((copy): copy is unknown[] => Array.isArray(copy)), controls]
      : [controls];
  };

  const everyControl = sections.flatMap((section) => instancesOf(section).flat());
  if (everyControl.length === 0 || everyControl.some((c) => isBlankQId(asRecord(c)?.q_id))) {
    return null;
  }

  const onRoute = new Set<unknown>();
  let nextFromPreviousSection: unknown = null;
  sections.forEach((section, sectionIndex) => {
    const instances = instancesOf(section);
    instances.forEach((controls, instanceIndex) => {
      const startsAtTop = (sectionIndex === 0 && instanceIndex === 0) || instances.length > 1;
      let next: unknown = startsAtTop
        ? (asRecord(controls[0])?.q_id ?? null)
        : nextFromPreviousSection;
      controls.forEach((control, controlIndex) => {
        const ctrl = asRecord(control);
        if (!ctrl) return;
        const reached =
          controlIndex === 0 ? startsAtTop || sameQId(next, ctrl.q_id) : sameQId(ctrl.q_id, next);
        if (!reached) return;
        onRoute.add(control);
        next = nextQIdIn(sections, ctrl);
      });
      if (instances.length === 1 || instanceIndex === instances.length - 1) {
        nextFromPreviousSection = next;
      }
    });
  });
  return onRoute;
}

/** Hidden or read-only questions aren't answered by the engineer, so they always show. */
const isFixedValue = (control: Record<string, unknown>): boolean =>
  control.hidden === true || control.read_only === true;

function parseSection(section: unknown, onRoute: Set<unknown> | null): ParsedFormSection[] {
  const sec = asRecord(section);
  if (!sec) return [];

  const sectionName = trimmedOrNull(sec.section_name) ?? '';

  if (Array.isArray(sec.controls) || Array.isArray(sec.repeated_sections)) {
    return instanceControls(sec)
      .filter((controls): controls is unknown[] => Array.isArray(controls))
      .map((controls) => {
        const answers: ParsedFormAnswer[] = [];
        for (const control of controls) {
          const ctrl = asRecord(control);
          if (!ctrl) continue;
          if (onRoute && !onRoute.has(control) && !isFixedValue(ctrl)) continue;
          const answer = parseControl(ctrl);
          if (answer) answers.push(answer);
          // Branch answers follow their parent question, so the panel reads in the order
          // the crew filled it in.
          answers.push(...parseBranchAnswers(ctrl));
          answers.push(...parseGroupAnswers(ctrl));
        }
        return { sectionName, answers };
      });
  }

  // Legacy flat shape: a single { question, answer } object.
  if ('question' in sec) {
    return [
      {
        sectionName,
        answers: [
          { question: String(sec.question ?? ''), answer: String(sec.answer ?? ''), images: [] },
        ],
      },
    ];
  }

  return [];
}

/**
 * Group the API's flat `form_responses` array into one entry per submitted form,
 * ordered by submission time (the order the crew actually completed them in).
 * Rows whose `form_data` is absent or non-object are skipped — the DB holds many
 * such placeholder rows for forms that were assigned but never filled in.
 */
export function parseFormResponses(rows: readonly FormResponseRow[]): ParsedFormGroup[] {
  const groups: ParsedFormGroup[] = [];

  for (const row of rows) {
    const formData = asRecord(row.form_data);
    if (!formData) {
      continue;
    }

    const onRoute = controlsOnRoute(formData);
    const sections: ParsedFormSection[] = [];
    for (const section of normaliseSections(formData)) {
      sections.push(...parseSection(section, onRoute));
    }

    groups.push({
      key: String(row.id),
      formName: resolveFormName(row, formData),
      submittedAt: row.created_time ?? null,
      sections,
    });
  }

  // Ascending by submission time; rows without a timestamp keep their original
  // relative order at the end. Index tiebreak keeps the sort stable.
  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      const aTime = a.group.submittedAt ? Date.parse(a.group.submittedAt) : Number.NaN;
      const bTime = b.group.submittedAt ? Date.parse(b.group.submittedAt) : Number.NaN;
      const aValid = !Number.isNaN(aTime);
      const bValid = !Number.isNaN(bTime);

      if (aValid && bValid && aTime !== bTime) {
        return aTime - bTime;
      }
      if (aValid !== bValid) {
        return aValid ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map(({ group }) => group);
}

/**
 * Total answer count across a form's sections — used to decide whether a panel
 * has anything worth showing.
 */
export function countAnswers(group: ParsedFormGroup): number {
  return group.sections.reduce((total, section) => total + section.answers.length, 0);
}
